import { useCallback, useRef } from 'react';
import { useDragDrop } from './useDragDrop';
import { useChartPositionContext } from '../contexts/ChartPositionContext';
import { ChartPosition } from '../contexts/ChartPositionContext';

export interface UsePositionDragOptions {
  onPositionMove?: (positionId: string, newPrice: number) => void;
  onSLUpdate?: (positionId: string, newPrice: number) => void;
  onTPUpdate?: (positionId: string, newPrice: number) => void;
}

export const usePositionDrag = (options: UsePositionDragOptions = {}) => {
  const { setSLLevel, setTPLevel, updateChartPosition } = useChartPositionContext();
  
  const handleConfirm = useCallback(() => {
    // This will be handled by the component that calls this hook
    // The component will calculate the price and call the appropriate update function
  }, []);

  const handleCancel = useCallback(() => {
    // Reset to original values - this will be handled by the component
    // console.log('Drag operation cancelled');
  }, []);

  const dragDrop = useDragDrop({
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  });

  // Helper function to determine if a position can be dragged
  const canDragPosition = useCallback((position: ChartPosition) => {
    // Only pending orders can be dragged (executed positions are fixed)
    return position.isPendingOrder === true;
  }, []);

  // Helper function to determine if SL/TP can be dragged
  const canDragSLTP = useCallback((position: ChartPosition) => {
    // Both executed and pending orders can have their SL/TP dragged
    return true;
  }, []);

  // Helper function to calculate drag constraints
  const getDragConstraints = useCallback((position: ChartPosition, type: 'sl' | 'tp') => {
    const entryPrice = parseFloat(position.entry);
    const isBuy = position.type === 'Buy';
    
    if (type === 'sl') {
      // For buy orders: SL should be below entry price
      // For sell orders: SL should be above entry price
      return {
        minPrice: isBuy ? 0 : entryPrice,
        maxPrice: isBuy ? entryPrice : Infinity,
        direction: isBuy ? 'below' : 'above'
      };
    } else {
      // For buy orders: TP should be above entry price
      // For sell orders: TP should be below entry price
      return {
        minPrice: isBuy ? entryPrice : 0,
        maxPrice: isBuy ? Infinity : entryPrice,
        direction: isBuy ? 'above' : 'below'
      };
    }
  }, []);

  return {
    ...dragDrop,
    canDragPosition,
    canDragSLTP,
    getDragConstraints,
  };
};
